import { Fragment, useRef, useEffect, useMemo, useState } from 'react';

import {
  SearchableVendorTaxonomyKey,
  VendorTaxonomyKey,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { setLocalStorage } from '@zola-helpers/client/dist/es/util/storage';
import { ErrorBoundary } from '@zola/zola-ui/src/components/ErrorBoundary';
import { useResponsiveDesign } from '@zola/zola-ui/src/contexts/ResponsiveDesignContext/ResponsiveDesignContext';
import useContainerDimensions from '@zola/zola-ui/src/hooks/useContainerDimensions';
import useForceUpdateOnWindowResize from '@zola/zola-ui/src/hooks/useForceUpdateOnWindowResize';
import { COLORS3 } from '@zola/zola-ui/src/styles/emotion';
import H from '@zola/zola-ui/src/typography/Headings';

import cx from 'classnames';

import SmallVendorCard, {
  hasRating,
} from '~/components/common/cards/SmallVendorCard/SmallVendorCard';
import { doTrackStorefrontListViewed } from '~/contexts/TrackingContracts/trackingContractsHelpers';
import { getRecentlyViewedVendorCardProps } from '~/pages/couples/explore/components/PersonalizedLandingPage/contexts/RecentlyViewedStorefrontsContext';
import { useSearchResultsPageContext } from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageContext';
import getCanonicalUrl from '~/util/storefrontUrl';
import uuidv4 from '~/util/uuidv4';

import { PageSection } from '../../../PageSection/PageSection';

import styles from './recentlyViewedStorefronts.module.less';

// Size of the image in the small vendor card, used to align the hed with the middle of the image.
const CARD_IMAGE_HEIGHT = 72;

export const RecentlyViewedStorefronts = () => {
  const { loading, vendorSearch, recentlyViewed } = useSearchResultsPageContext();
  const { isDesktop } = useResponsiveDesign();

  const { selectedLocation } = vendorSearch;
  const queriedLocation =
    selectedLocation?.type !== 'NO_MARKETS' ? selectedLocation?.label : undefined;

  // This handy little fucker forces useContainerDimensions to update.  The ref to the
  // div doesn't change, but we change the content within the div, which changes
  // the width.  We use a _state_ transition (just using the selected taxonomy
  // was not enough) to force the effect hook in useContainerDimensions to re-execute.
  const [guid, setGuid] = useState<string>();
  useEffect(() => {
    setGuid(uuidv4());
  }, [vendorSearch.selectedTaxonomy?.key, recentlyViewed]);

  const shelfRef = useRef<HTMLDivElement>(null);
  const { scrollWidth, width } = useContainerDimensions(shelfRef, guid);

  useEffect(() => {
    if (recentlyViewed.length > 0) {
      doTrackStorefrontListViewed({
        storefrontList: recentlyViewed,
        listingShelfName: 'RECENTLY_VIEWED',
        taxonomyKey: vendorSearch.selectedTaxonomy?.key as SearchableVendorTaxonomyKey | undefined,
      });
    }
  }, [vendorSearch.selectedTaxonomy?.key, recentlyViewed]);

  useForceUpdateOnWindowResize();

  const hasARatedVendor = useMemo(() => {
    return recentlyViewed.some((vendor) => hasRating(vendor));
  }, [recentlyViewed]);

  const scrollActive = useMemo(() => {
    return scrollWidth > width;
  }, [scrollWidth, width]);

  if (recentlyViewed.length === 0) {
    return <Fragment />;
  }

  return (
    <ErrorBoundary>
      <PageSection
        backgroundColor={COLORS3.CLOUD_100}
        className={styles.recentlyViewedListings}
        noPadding
      >
        <div
          className={cx(styles.recentlyViewedWrapper, {
            [styles.loading]: loading || scrollWidth === 0,
          })}
        >
          <div
            className={styles.hedWrapper}
            style={{
              height: isDesktop ? CARD_IMAGE_HEIGHT : undefined,
            }}
          >
            <H.Title5 className={styles.hed}>Recently viewed</H.Title5>
          </div>

          <div className={styles.recentlyViewedShelf} ref={shelfRef}>
            {recentlyViewed.map((vendor, index) => {
              return (
                <SmallVendorCard
                  // -----------------------------------------------------------
                  //
                  // If you change the props here (adding more rows of data, make
                  // sure to update DEFAULT_CARD_HEIGHT so the page doesn't jump around)
                  //
                  // -----------------------------------------------------------
                  className={cx(styles.recentlyViewedListing, {
                    [styles.scrollActive]: scrollActive,
                  })}
                  key={vendor.storefrontUuid}
                  storefront={{
                    name: vendor.vendorName,
                    type: vendor.taxonomyNodeKey as VendorTaxonomyKey,
                    vendorCard: vendor,
                  }}
                  v2
                  showRating
                  showCategory={vendorSearch.selectedTaxonomy?.key === undefined}
                  alignCenter={!hasARatedVendor}
                  showPricing={false}
                  internalLink={getCanonicalUrl(
                    vendor.taxonomyNodeKey as VendorTaxonomyKey,
                    vendor.storefrontSlug as string
                  )}
                  {...getRecentlyViewedVendorCardProps(vendor, queriedLocation)}
                  trackClick
                  position={index + 1}
                  listingShelfName="RECENTLY_VIEWED"
                  onClick={() => {
                    setLocalStorage(
                      `inquiry_entrypoint_${vendor.storefrontUuid}`,
                      'RECENTLY_VIEWED_VENDORS_SRP'
                    );
                  }}
                  // -----------------------------------------------------------
                  //
                  // If you change the props here (adding more rows of data, make
                  // sure to update DEFAULT_CARD_HEIGHT so the page doesn't jump around)
                  //
                  // -----------------------------------------------------------
                />
              );
            })}
          </div>
          <div className={styles.scrim} />
        </div>
      </PageSection>
    </ErrorBoundary>
  );
};
